import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { select_history_item } from 'src/redux/store'
import { Callbacks, DataEntryStep1Props, InputRadioOption, coverages_t, factors_t, periods_t } from 'src/types'
import { addStartDays, formatDate, get_language, reverseDate, useBoolean } from 'src/utils'

export const useDataEntryStep1 = (props: DataEntryStep1Props) => {
  const { t } = useTranslation()
  const { minMaxContractor, formik } = props

  const {
    values: {
      insured_days,
      coverage_start_date,
      insured_daily,
      coverage_type_radios,
      coverage_radios
    },
    setFieldValue
  } = formik;

  const periods: periods_t = useSelector(select_history_item('history_periods'))

  const MIN_DATE_PERIOD_TEMP = periods && periods['3']?.min ? periods['3']?.min : 0
  const MAX_DATE_PERIOD_TEMP = periods && periods['3']?.max ? periods['3']?.max - 1 : 0
  const MAX_DATE_WEEKLY_TEMP = periods && periods["4"]?.max ? periods["4"]?.max - 1 : 0;
  const MAX_DATE_MONTHLY_TEMP = periods && periods["5"]?.max ? periods["5"]?.max - 1 : 0;
  const MAX_DATE_QUARTERLY_TEMP = periods && periods["6"]?.max ? periods["6"]?.max - 1 : 0;

  const factors: factors_t = useSelector(select_history_item('history_factors'))
  const seasonalEndDate = new Date(reverseDate(factors?.['2']?.maxDate || '', '/', '-'))
  const coverageStartDate = reverseDate(new Date().toString())
  const coverageStartDatePeriod = formatDate(addStartDays(coverageStartDate, MIN_DATE_PERIOD_TEMP))

  const coverageStartDateDisabledFrom = new Date()
  const coverageStartDateDisabledTo = seasonalEndDate

  const coverageEndDateDisabledFrom = new Date(reverseDate(coverageStartDatePeriod, '/', '-'))
  const coverageEndDateDisabledTo = new Date(
    reverseDate(formatDate(addStartDays(coverageStartDate, MAX_DATE_PERIOD_TEMP)), '/', '-')
  )

  const [closePickerTrigger, , , toggleClosePickerTrigger] = useBoolean()
  /**
   * useSelector
   */
  const coverages: coverages_t = useSelector(select_history_item('history_coverages'))

  /**
   * useState
   */
  const [coverageOptions, setCoverageOptions] = useState<InputRadioOption[]>([])

  /**
   * useEffect
   */
  useEffect(() => {
    if (coverages) {
      const coverages_: InputRadioOption[] = Object.entries(coverages).map(([key, value]) => {
        return {
          id: key,
          value: value?.name?.[get_language().toLowerCase()] || '',
        }
      })
      setCoverageOptions(coverages_)
    }
  }, [coverages])

  useEffect(() => {
    if (insured_days) {
      setFieldValue('coverage_start_date', moment().format('DD/MM/YYYY'))
      toggleClosePickerTrigger()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insured_days])

  useEffect(() => {
    toggleClosePickerTrigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverage_type_radios])

  useEffect(() => {
    if (insured_days === 'daily') {
      if (insured_daily === "daily_1")
        setFieldValue('coverage_radios', 'id_daily')
      else
        setFieldValue('coverage_radios', 'id_period')
    } else if (insured_days === 'weekly_7') {
      setFieldValue('coverage_radios', 'id_weekly')
    } else if (insured_days === 'monthly_30') {
      setFieldValue('coverage_radios', 'id_monthly')
    } else if (insured_days === 'quarterly_90') {
      setFieldValue('coverage_radios', 'id_quarterly')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insured_days, insured_daily])

  useEffect(() => {
    if (coverage_start_date) {
      const callbacks: Callbacks = {
        daily: () => {
          const days = Number(insured_daily.split("_")[1])
          if (days <= 4)
            setFieldValue(
              "coverage_end_date",
              formatDate(addStartDays(reverseDate(coverage_start_date), days - 1))
            );
        },
        weekly_7: () => {
          setFieldValue(
            "coverage_end_date",
            formatDate(
              addStartDays(
                reverseDate(coverage_start_date),
                MAX_DATE_WEEKLY_TEMP || 0
              )
            )
          );
        },
        monthly_30: () => {
          setFieldValue(
            "coverage_end_date",
            formatDate(
              addStartDays(
                reverseDate(coverage_start_date),
                MAX_DATE_MONTHLY_TEMP || 0
              )
            )
          );
        },
        quarterly_90: () => {
          setFieldValue(
            "coverage_end_date",
            formatDate(
              addStartDays(
                reverseDate(coverage_start_date),
                MAX_DATE_QUARTERLY_TEMP || 0
              )
            )
          );
        }
      };
      callbacks[insured_days]?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverage_start_date, coverage_radios, insured_daily]);

  return {
    t,
    coverageOptions,
    minMaxContractor,
    coverageStartDateDisabledFrom,
    coverageStartDateDisabledTo,
    coverageEndDateDisabledFrom,
    coverageEndDateDisabledTo,
    closePickerTrigger,
    toggleClosePickerTrigger,
  }
}
